import { createFileRoute, notFound } from '@tanstack/react-router';
import { getEndpoints, type EndpointInfo, type OpenApiParameter, type OpenApiSchema } from '@/api/client';
import { endpointSlug } from '@/lib/slug';
import { MethodBadge } from '@/components/playground/MethodBadge';
import { TryItForm } from '@/components/playground/TryItForm';
import { Copy, FileCode } from 'lucide-react';

export const Route = createFileRoute('/endpoints/$tag/$operation')({
  loader: ({ params }) => {
    const all = getEndpoints();
    const endpoint = all.find((e) => endpointSlug(e.operationId) === params.operation);
    if (!endpoint) throw notFound();
    return { endpoint };
  },
  component: EndpointDetailPage,
  notFoundComponent: () => (
    <div className="p-12 text-fg-tertiary text-sm">
      Endpoint not found. <a href="/" className="text-accent hover:underline">Back to home</a>.
    </div>
  ),
});

function EndpointDetailPage() {
  const { endpoint } = Route.useLoaderData();

  return (
    <div className="max-w-3xl mx-auto px-8 py-10">
      <Header endpoint={endpoint} />

      {endpoint.description && (
        <p className="mt-4 text-fg-secondary leading-relaxed whitespace-pre-line">
          {endpoint.description}
        </p>
      )}

      <UrlBar endpoint={endpoint} />

      <SourceLink endpoint={endpoint} />

      <ParametersSection params={pathParams(endpoint)} title="Path parameters" />
      <ParametersSection params={queryParams(endpoint)} title="Query parameters" />
      <ParametersSection params={headerParams(endpoint)} title="Header parameters" />

      <RequestBodySection endpoint={endpoint} />

      <ResponsesSection endpoint={endpoint} />

      <TryItForm endpoint={endpoint} />
    </div>
  );
}

function Header({ endpoint }: { endpoint: EndpointInfo }) {
  return (
    <div className="space-y-3">
      <div className="inline-flex items-center gap-2 px-3 h-7 rounded-pill border-2 border-border-strong bg-method-put text-2xs font-mono font-bold uppercase tracking-wider shadow-brutal-sm">
        {endpoint.tag}
      </div>
      <h1 className="font-display font-extrabold tracking-tight text-3xl md:text-4xl">
        {endpoint.summary}
      </h1>
    </div>
  );
}

function UrlBar({ endpoint }: { endpoint: EndpointInfo }) {
  return (
    <div className="mt-8 flex items-center gap-3 px-3 h-12 rounded-pill border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm font-mono text-sm">
      <MethodBadge method={endpoint.method} size="md" />
      <span className="flex-1 text-fg truncate font-semibold">{endpoint.path}</span>
      <button
        type="button"
        title="Copy path"
        onClick={() => navigator.clipboard.writeText(endpoint.path)}
        className="w-8 h-8 rounded-pill border-2 border-border-strong bg-base shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all flex items-center justify-center"
      >
        <Copy size={13} />
      </button>
    </div>
  );
}

function SourceLink({ endpoint }: { endpoint: EndpointInfo }) {
  const src = endpoint.operation['x-source-file'];
  if (!src) return null;
  return (
    <div className="mt-3 flex items-center gap-1.5 text-2xs text-fg-tertiary">
      <FileCode size={11} />
      <span className="font-mono">{src}</span>
    </div>
  );
}

function pathParams(ep: EndpointInfo): OpenApiParameter[] {
  return (ep.operation.parameters || []).filter((p) => p.in === 'path');
}
function queryParams(ep: EndpointInfo): OpenApiParameter[] {
  return (ep.operation.parameters || []).filter((p) => p.in === 'query');
}
function headerParams(ep: EndpointInfo): OpenApiParameter[] {
  return (ep.operation.parameters || []).filter((p) => p.in === 'header');
}

function ParametersSection({
  params,
  title,
}: {
  params: OpenApiParameter[];
  title: string;
}) {
  if (params.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="font-display font-extrabold text-lg mb-3">{title}</h2>
      <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm divide-y-2 divide-border-strong overflow-hidden">
        {params.map((p) => (
          <div key={`${p.in}-${p.name}`} className="px-4 py-3 flex items-baseline gap-3">
            <code className="font-mono font-bold text-sm text-fg shrink-0">{p.name}</code>
            <span className="inline-flex items-center px-2 h-5 rounded-pill border-2 border-border-strong bg-base text-2xs font-mono font-bold">
              {p.schema?.type || 'string'}
            </span>
            {p.required && (
              <span className="inline-flex items-center px-2 h-5 rounded-pill border-2 border-border-strong bg-method-delete text-2xs font-mono font-bold">
                required
              </span>
            )}
            <span className="flex-1 text-sm text-fg-secondary leading-snug">
              {p.description}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RequestBodySection({ endpoint }: { endpoint: EndpointInfo }) {
  const body = endpoint.operation.requestBody;
  if (!body) return null;
  const schema = body.content?.['application/json']?.schema;
  return (
    <section className="mt-10">
      <h2 className="font-display font-extrabold text-lg mb-3">Request body</h2>
      {body.description && (
        <p className="text-sm text-fg-secondary mb-3">{body.description}</p>
      )}
      <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm px-4 py-3 font-mono text-2xs text-fg-secondary leading-relaxed">
        {schema ? <SchemaTree schema={schema} /> : <span className="text-fg-tertiary">(no schema declared)</span>}
      </div>
    </section>
  );
}

function ResponsesSection({ endpoint }: { endpoint: EndpointInfo }) {
  const responses = endpoint.operation.responses || {};
  const codes = Object.keys(responses);
  if (codes.length === 0) return null;

  return (
    <section className="mt-10 mb-10">
      <h2 className="font-display font-extrabold text-lg mb-3">Responses</h2>
      <div className="rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm divide-y-2 divide-border-strong overflow-hidden">
        {codes.map((code) => {
          const r = responses[code];
          const isOk = code.startsWith('2');
          return (
            <div key={code} className="px-4 py-3 flex items-baseline gap-3">
              <span
                className={
                  'inline-flex items-center px-2.5 h-6 rounded-pill border-2 border-border-strong font-mono font-bold text-2xs ' +
                  (isOk ? 'bg-method-get' : 'bg-method-delete')
                }
              >
                {code}
              </span>
              <span className="flex-1 text-sm text-fg-secondary leading-snug">
                {r.description || '—'}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Recursive renderer for JSON Schema. Handles object/array/scalar trees.
 * Kept intentionally minimal — Phase 2 will add a richer collapsible viewer.
 */
function SchemaTree({ schema, depth = 0 }: { schema: OpenApiSchema; depth?: number }) {
  const indent = '  '.repeat(depth);
  if (schema.type === 'object' || schema.properties) {
    const props = schema.properties || {};
    const requiredSet = new Set(schema.required || []);
    const keys = Object.keys(props);
    if (keys.length === 0) {
      return <span>{'{ }'}</span>;
    }
    return (
      <div>
        <div>{indent}{'{'}</div>
        {keys.map((key) => (
          <div key={key}>
            {indent}  <span className="text-fg">{key}</span>
            {requiredSet.has(key) && <span className="text-error">!</span>}
            <span className="text-fg-muted">: </span>
            <SchemaTree schema={props[key]} depth={depth + 1} />
            {props[key].description && (
              <span className="text-fg-muted"> // {props[key].description}</span>
            )}
          </div>
        ))}
        <div>{indent}{'}'}</div>
      </div>
    );
  }
  if (schema.type === 'array') {
    return (
      <span>
        <span className="text-method-put">array</span>
        {schema.items && (
          <>
            {' of '}
            <SchemaTree schema={schema.items} depth={depth} />
          </>
        )}
      </span>
    );
  }
  return (
    <span className="text-method-put">
      {schema.type || 'any'}
      {schema.format ? ` (${schema.format})` : ''}
    </span>
  );
}
